import { ReactElement, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import {
  competitions,
  gameSportcastId,
  gameSportcastIdWhere,
  regions,
  sports
} from 'utils/constants/sportsbook/data-fields';
import { getMountPath } from 'utils/generic/route-manipulation';
import { SPORTSBOOK_ROUTE_WITH_CATEGORY } from 'utils/sportsbook/route.structure';
import { getLivePreMatchSportTypes } from 'utils/sportsbook/sport-data';
import RidGenerator from 'utils/swarm/rid-generator';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import { CompetitionCollapse } from './CompetitionCollapse';
import { CompetitionSkeleton } from 'components/UI/Skeletons/CompetitionSkeleton';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useMounted } from 'hooks/useMounted';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setSportRegionCompData } from 'store/actions/new-sportsbook';
import {
  getShowNoItemWrapper,
  getSportRegionCompData
} from 'store/selectors/new-sportsbook';
import { getPrematchBoostedOddsIds } from 'store/selectors/sport-data';
import './index.less';

type TBoostedOddsLayoutProps = {
  sportCategory: ENotRealSports;
};

export const GamesByCompetitionsCollapse = ({
  sportCategory
}: TBoostedOddsLayoutProps): ReactElement | null => {
  const configs = useSportsbookConfigs();

  const dispatch = useDispatch();
  const history = useHistory();
  const { mounted } = useMounted();
  const routeParams = useSbRouteMatch(SPORTSBOOK_ROUTE_WITH_CATEGORY);

  const sportAlias = routeParams?.params.sport || '';
  const reduxKey = `${sportCategory}_${sportAlias}`;

  const boostedGameIDs = useSelector(getPrematchBoostedOddsIds);
  const showNoItemWrapper = useSelector(getShowNoItemWrapper);
  const gamesData = useSelector(getSportRegionCompData)?.[reduxKey];

  useEffect(() => {
    let shouldUnsubscribe = false;

    if (!sportAlias) {
      return;
    }

    if (sportCategory === ENotRealSports.boostedOdds && !boostedGameIDs) {
      return;
    }

    shouldUnsubscribe = true;
    const where: Record<string, any> = {
      game: {
        type: {
          '@in': [0, 2]
        },
        id: {
          '@in': boostedGameIDs
        }
      },
      sport: {
        alias: sportAlias
      },
      market: {
        '@or': [
          {
            display_key: { '@in': ['HANDICAP', 'TOTALS'] },
            display_sub_key: 'MATCH',
            main_order: 1
          },
          { display_key: 'WINNER', display_sub_key: 'MATCH' },
          { display_key: 'OUTRIGHT' }
        ]
      }
    };

    if (configs.sportType) {
      where.sport = {
        ...where.sport,
        type: {
          '@in': getLivePreMatchSportTypes(configs.sportType)
        }
      };
    }

    const what = {
      region: regions.region,
      competition: competitions.competition,
      sport: sports.sport,
      game: [
        'id',
        'team1_name',
        'team2_name',
        'team1_id',
        'team2_id',
        'order',
        'start_ts',
        'markets_count',
        'is_blocked',
        'show_type',
        'sportcast_id',
        'sport_id',
        'is_stat_available'
      ],
      market: [
        'name',
        'order',
        'type',
        'id',
        'base',
        'express_id',
        'col_count',
        'main_order'
      ],
      event: ['name', 'id', 'price', 'base', 'order', 'type_1']
    };

    if (sportCategory === ENotRealSports.betBuilder) {
      delete where.game.id;

      where.game.sportcast_id = gameSportcastIdWhere;

      if (!what.game.includes(gameSportcastId[0])) {
        what.game.push(gameSportcastId[0]);
      }
    }

    if (sportCategory === ENotRealSports.topMatches) {
      delete where.game.id;

      where.game.promoted = true;
    }

    const Rid = RidGenerator.gForSubscribe(`${sportCategory}_GAMES`);

    executeCommand(
      Rid,
      what,
      where,
      (resp: { sport: Record<string, Sport> }) => {
        if (mounted.current && resp.sport && Object.keys(resp.sport).length) {
          dispatch(
            setSportRegionCompData({
              [reduxKey]: resp.sport[Object.keys(resp.sport)[0]] || null
            })
          );
        }
      }
    );

    return () => {
      if (shouldUnsubscribe) {
        unsubscribeCommand(Rid);
      }
    };
  }, [boostedGameIDs, sportCategory, sportAlias]);

  const handleMatchCardClick = useCallback(
    (
      sportAlias: string,
      regionAlias: string,
      compId: number,
      gameId: number
    ) => {
      if (routeParams?.path) {
        const newPath = `${getMountPath(
          routeParams?.path
        )}/${sportCategory}/${sportAlias}/${regionAlias}/${compId}/${gameId}`;

        history.push(newPath);
      }
    },
    [routeParams?.path, sportCategory]
  );

  if (showNoItemWrapper?.competitions) {
    return null;
  }

  return gamesData && sportAlias ? (
    <div className="competitionsCollapseWrapper">
      {sortByOrder(Object.values(gamesData.region || {})).map(
        (region, regIndex) =>
          sortByOrder(Object.values(region.competition || {})).map(
            (comp, compIndex) => (
              <CompetitionCollapse
                key={comp.id}
                onClick={handleMatchCardClick}
                sportAlias={sportAlias}
                sportName={gamesData.name}
                regionAlias={region.alias}
                compId={comp.id}
                compName={comp.name}
                isActive={Number(routeParams?.params.competition) === comp.id}
                games={comp.game || {}}
                isFirstCompetition={regIndex === 0 && compIndex === 0}
                selectedCompetition={routeParams?.params.competition}
                activeGameId={routeParams?.params.game || ''}
                comp={comp}
              />
            )
          )
      )}
    </div>
  ) : (
    <CompetitionSkeleton
      sportAlias={sportAlias}
      includeCollapse
      hideMargin
      className={
        [ENotRealSports.boostedOdds, ENotRealSports.topMatches].includes(
          sportCategory
        )
          ? 'competitionWithSports__skeletonContainer'
          : ''
      }
      count={4}
    />
  );
};
